import { useEffect, useState } from 'react';
import {
  createPositecClient,
  discoverConnection,
  type AuthTenant,
  type AuthUser,
  type DiscoveryResult,
  type FiscalCertificate,
  type FiscalRecord,
  type FiscalSettings,
  type PositecClient,
} from '@positec/api-client';

const TOKEN_KEY = 'positec_admin_token';
const CLOUD_URL = import.meta.env.VITE_API_URL ?? 'https://api.itpos.itrionsoft.com';

function buildClient(baseURL: string): PositecClient {
  return createPositecClient({
    baseURL,
    getToken: () => localStorage.getItem(TOKEN_KEY),
    onUnauthenticated: () => localStorage.removeItem(TOKEN_KEY),
  });
}

type Session = { user: AuthUser; tenant: AuthTenant | null };

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [discovery, setDiscovery] = useState<DiscoveryResult>({
    baseURL: CLOUD_URL,
    mode: 'cloud',
  });
  const [client, setClient] = useState<PositecClient>(() => buildClient(CLOUD_URL));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const disc = await discoverConnection({ cloudURL: CLOUD_URL });
      if (cancelled) return;
      setDiscovery(disc);
      const c = buildClient(disc.baseURL);
      setClient(c);

      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const r = await c.me();
        if (cancelled) return;
        setSession({ user: r.user, tenant: r.tenant });
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-400">Cargando…</div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header
        session={session}
        client={client}
        connection={discovery}
        onLogout={() => setSession(null)}
      />
      <main className="flex-1 overflow-auto p-6">
        {session ? (
          <Dashboard session={session} client={client} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <LoginForm client={client} onSuccess={setSession} />
          </div>
        )}
      </main>
    </div>
  );
}

function Header({
  session,
  client,
  connection,
  onLogout,
}: {
  session: Session | null;
  client: PositecClient;
  connection: DiscoveryResult;
  onLogout: () => void;
}) {
  return (
    <header className="flex items-center justify-between border-b border-slate-800 bg-slate-950 px-6 py-3">
      <div className="flex items-center gap-3">
        <span className="text-lg font-semibold tracking-tight">
          IT<span className="text-positec-accent">pos</span>
        </span>
        <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400">admin</span>
        <ConnectionBadge mode={connection.mode} />
      </div>
      {session && (
        <div className="flex items-center gap-3 text-sm text-slate-300">
          <span>{session.user.email}</span>
          <button
            onClick={async () => {
              try {
                await client.logout();
              } catch {}
              localStorage.removeItem(TOKEN_KEY);
              onLogout();
            }}
            className="rounded border border-slate-700 px-3 py-1 text-xs hover:bg-slate-800"
          >
            Salir
          </button>
        </div>
      )}
    </header>
  );
}

function ConnectionBadge({ mode }: { mode: DiscoveryResult['mode'] }) {
  const label = mode === 'bridge' ? '🟢 Bridge' : mode === 'cloud' ? '☁️ Cloud' : '⚠️ Sin red';
  return <span className="text-xs text-slate-400">{label}</span>;
}

function LoginForm({
  client,
  onSuccess,
}: {
  client: PositecClient;
  onSuccess: (s: Session) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await client.login({ email, password });
      localStorage.setItem(TOKEN_KEY, r.token);
      onSuccess({ user: r.user, tenant: r.tenant });
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e.message ?? 'Error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-sm space-y-3 rounded-lg border border-slate-800 bg-slate-900 p-6"
    >
      <h1 className="text-lg font-medium">Acceso al panel</h1>
      <input
        autoFocus
        type="email"
        placeholder="email@dominio"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
      />
      <input
        type="password"
        placeholder="contraseña"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
      />
      {err && <p className="rounded bg-red-950/40 px-3 py-2 text-xs text-red-300">{err}</p>}
      <button
        disabled={busy || !email || !password}
        className="w-full rounded bg-positec-accent py-2 text-sm font-medium text-slate-900 disabled:opacity-50"
      >
        {busy ? 'Entrando…' : 'Entrar'}
      </button>
    </form>
  );
}

type DashSection = 'overview' | 'fiscal' | 'reports' | 'multilocal';

function Dashboard({ session, client }: { session: Session; client: PositecClient }) {
  const [section, setSection] = useState<DashSection>('overview');
  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <nav className="flex gap-2 border-b border-slate-800 pb-3 text-sm">
        <button
          onClick={() => setSection('overview')}
          className={`rounded px-3 py-1 ${section === 'overview' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Resumen
        </button>
        <button
          onClick={() => setSection('reports')}
          className={`rounded px-3 py-1 ${section === 'reports' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Informes
        </button>
        <button
          onClick={() => setSection('multilocal')}
          className={`rounded px-3 py-1 ${section === 'multilocal' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Multilocal
        </button>
        <button
          onClick={() => setSection('fiscal')}
          className={`rounded px-3 py-1 ${section === 'fiscal' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Fiscal (VeriFactu / Ticket BAI)
        </button>
      </nav>
      {section === 'overview' && <Overview session={session} />}
      {section === 'fiscal' && <FiscalPanel client={client} />}
      {section === 'reports' && <ReportsPanel client={client} />}
      {section === 'multilocal' && <MultilocalPanel client={client} />}
    </div>
  );
}

function Overview({ session }: { session: Session }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-6">
        <h2 className="mb-4 text-lg font-medium">Sesión activa</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <dt className="text-slate-400">Usuario</dt>
          <dd>{session.user.name}</dd>
          <dt className="text-slate-400">Email</dt>
          <dd>{session.user.email}</dd>
          <dt className="text-slate-400">Tipo</dt>
          <dd>
            <code className="rounded bg-slate-800 px-2 py-0.5 text-xs">
              {session.user.user_type}
            </code>
          </dd>
        </dl>
      </div>
      {session.tenant && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-6">
          <h2 className="mb-4 text-lg font-medium">Tenant</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <dt className="text-slate-400">Nombre</dt>
            <dd>{session.tenant.name}</dd>
            <dt className="text-slate-400">Slug</dt>
            <dd>
              <code className="rounded bg-slate-800 px-2 py-0.5 text-xs">
                {session.tenant.slug}
              </code>
            </dd>
            <dt className="text-slate-400">Estado</dt>
            <dd>{session.tenant.status}</dd>
            <dt className="text-slate-400">Saldo tickets</dt>
            <dd className="font-mono text-positec-accent">
              {session.tenant.ticket_balance.toLocaleString('es-ES')}
            </dd>
          </dl>
        </div>
      )}
    </div>
  );
}

function FiscalPanel({ client }: { client: PositecClient }) {
  const [settings, setSettings] = useState<FiscalSettings | null>(null);
  const [certs, setCerts] = useState<FiscalCertificate[]>([]);
  const [records, setRecords] = useState<FiscalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'settings' | 'certs' | 'records'>('settings');

  async function loadAll() {
    setLoading(true);
    setErr(null);
    try {
      const [s, c, r] = await Promise.all([
        client.getFiscalSettings(),
        client.listFiscalCertificates(),
        client.listFiscalRecords({ per_page: 50 }),
      ]);
      setSettings(s);
      setCerts(c.certificates);
      setRecords(r.records);
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e.message ?? 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  if (loading) return <p className="text-slate-400">Cargando datos fiscales…</p>;
  if (err) return <p className="rounded bg-red-950/40 px-3 py-2 text-xs text-red-300">{err}</p>;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 text-sm">
        <button onClick={() => setTab('settings')} className={`rounded px-3 py-1 ${tab === 'settings' ? 'bg-slate-800' : 'text-slate-400'}`}>Configuración</button>
        <button onClick={() => setTab('certs')} className={`rounded px-3 py-1 ${tab === 'certs' ? 'bg-slate-800' : 'text-slate-400'}`}>Certificados ({certs.length})</button>
        <button onClick={() => setTab('records')} className={`rounded px-3 py-1 ${tab === 'records' ? 'bg-slate-800' : 'text-slate-400'}`}>Registros ({records.length})</button>
      </div>

      {tab === 'settings' && settings && <FiscalSettingsForm settings={settings} certs={certs} client={client} onSaved={(s) => setSettings(s)} />}
      {tab === 'certs' && <FiscalCertList certs={certs} client={client} onChanged={loadAll} />}
      {tab === 'records' && <FiscalRecordList records={records} client={client} onChanged={loadAll} />}
    </div>
  );
}

function FiscalSettingsForm({ settings, certs, client, onSaved }: { settings: FiscalSettings; certs: FiscalCertificate[]; client: PositecClient; onSaved: (s: FiscalSettings) => void }) {
  const [draft, setDraft] = useState<Partial<FiscalSettings>>(settings);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const updated = await client.updateFiscalSettings(draft);
      onSaved(updated);
      setMsg('Guardado');
    } catch (e: any) {
      setMsg(e?.response?.data?.message ?? 'Error guardando');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/50 p-6">
      <h3 className="text-base font-medium">Configuración fiscal</h3>
      <Field label="Modo VeriFactu">
        <select value={draft.verifactu_mode ?? 'disabled'} onChange={(e) => setDraft({ ...draft, verifactu_mode: e.target.value as any })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm">
          <option value="disabled">Deshabilitado (solo simplificadas no fiscales)</option>
          <option value="no_remitido">No remitido (firma + hash chain pero sin envío)</option>
          <option value="remitido">Remitido (envía a AEAT en tiempo real)</option>
        </select>
      </Field>
      <Field label="Territorio Ticket BAI">
        <select value={draft.ticket_bai_territory ?? ''} onChange={(e) => setDraft({ ...draft, ticket_bai_territory: (e.target.value || null) as any })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm">
          <option value="">— ninguno (territorio común) —</option>
          <option value="bizkaia">Bizkaia</option>
          <option value="gipuzkoa">Gipuzkoa</option>
          <option value="alava">Álava</option>
        </select>
      </Field>
      <Field label="Certificado por defecto">
        <select value={draft.default_certificate_id ?? ''} onChange={(e) => setDraft({ ...draft, default_certificate_id: e.target.value || null })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm">
          <option value="">— sin elegir —</option>
          {certs.filter(c => c.is_active).map(c => (
            <option key={c.id} value={c.id}>{c.alias} ({c.subject_cn ?? c.id.slice(0, 8)})</option>
          ))}
        </select>
      </Field>
      <Field label="Entorno de pruebas">
        <input type="checkbox" checked={!!draft.is_test_environment} onChange={(e) => setDraft({ ...draft, is_test_environment: e.target.checked })} />
        <span className="text-xs text-slate-400 ml-2">Si está marcado, no se envía a Hacienda real (modo stub).</span>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="NIF emisor">
          <input value={draft.emisor_nif ?? ''} onChange={(e) => setDraft({ ...draft, emisor_nif: e.target.value })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm" />
        </Field>
        <Field label="Razón social emisor">
          <input value={draft.emisor_nombre ?? ''} onChange={(e) => setDraft({ ...draft, emisor_nombre: e.target.value })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm" />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Software ID"><input value={draft.software_id ?? ''} onChange={(e) => setDraft({ ...draft, software_id: e.target.value })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm" /></Field>
        <Field label="Software nombre"><input value={draft.software_name ?? ''} onChange={(e) => setDraft({ ...draft, software_name: e.target.value })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm" /></Field>
        <Field label="Software versión"><input value={draft.software_version ?? ''} onChange={(e) => setDraft({ ...draft, software_version: e.target.value })} className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm" /></Field>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button disabled={busy} onClick={save} className="rounded bg-positec-accent px-4 py-2 text-sm font-medium text-slate-900 disabled:opacity-50">
          {busy ? 'Guardando…' : 'Guardar'}
        </button>
        {msg && <span className="text-xs text-slate-400">{msg}</span>}
      </div>
    </div>
  );
}

function FiscalCertList({ certs, client, onChanged }: { certs: FiscalCertificate[]; client: PositecClient; onChanged: () => void }) {
  const [alias, setAlias] = useState('');
  const [password, setPassword] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function upload() {
    if (!file || !alias || !password) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('alias', alias);
      fd.append('password', password);
      fd.append('pfx_file', file);
      await client.uploadFiscalCertificate(fd);
      setAlias(''); setPassword(''); setFile(null);
      onChanged();
      setMsg('Certificado subido');
    } catch (e: any) {
      setMsg(e?.response?.data?.message ?? 'Error subiendo certificado');
    } finally {
      setBusy(false);
    }
  }

  async function del(id: string) {
    if (!confirm('¿Eliminar certificado?')) return;
    try { await client.deleteFiscalCertificate(id); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.message ?? 'Error'); }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 space-y-2">
        <h4 className="text-sm font-medium">Subir nuevo certificado PFX</h4>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <input placeholder="Alias" value={alias} onChange={(e) => setAlias(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-3 py-2" />
          <input type="password" placeholder="Contraseña del PFX" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-3 py-2" />
          <input type="file" accept=".pfx,.p12" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-xs" />
        </div>
        <div className="flex items-center gap-3">
          <button disabled={busy || !file || !alias || !password} onClick={upload} className="rounded bg-positec-accent px-4 py-1.5 text-sm font-medium text-slate-900 disabled:opacity-50">
            {busy ? 'Subiendo…' : 'Subir'}
          </button>
          {msg && <span className="text-xs text-slate-400">{msg}</span>}
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-500">
          <tr><th className="text-left p-2">Alias</th><th className="text-left p-2">CN</th><th className="text-left p-2">Caduca</th><th className="text-left p-2">Estado</th><th></th></tr>
        </thead>
        <tbody>
          {certs.map(c => (
            <tr key={c.id} className="border-t border-slate-800">
              <td className="p-2">{c.alias}</td>
              <td className="p-2 text-slate-400">{c.subject_cn ?? '—'}</td>
              <td className="p-2 text-slate-400 font-mono text-xs">{c.valid_to?.slice(0, 10) ?? '—'}</td>
              <td className="p-2">{c.is_active ? <span className="rounded bg-green-950 px-2 py-0.5 text-xs text-green-400">activo</span> : <span className="text-slate-500 text-xs">inactivo</span>}</td>
              <td className="p-2 text-right"><button onClick={() => del(c.id)} className="text-xs text-red-400 hover:text-red-300">Borrar</button></td>
            </tr>
          ))}
          {certs.length === 0 && <tr><td colSpan={5} className="text-slate-500 italic p-3 text-center">Sin certificados subidos</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FiscalRecordList({ records, client, onChanged }: { records: FiscalRecord[]; client: PositecClient; onChanged: () => void }) {
  async function retry(id: string) {
    try { await client.retrySendFiscalRecord(id); onChanged(); }
    catch (e: any) { alert(e?.response?.data?.message ?? 'Error'); }
  }
  const statusColor = (s: string) =>
    s === 'accepted' ? 'bg-green-950 text-green-400'
    : s === 'rejected' ? 'bg-red-950 text-red-400'
    : s === 'sent' ? 'bg-yellow-950 text-yellow-400'
    : 'bg-slate-800 text-slate-300';

  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase text-slate-500">
        <tr><th className="text-left p-2">#</th><th className="text-left p-2">Tipo</th><th className="text-left p-2">Emitido</th><th className="text-left p-2">Estado</th><th className="text-left p-2">QR</th><th></th></tr>
      </thead>
      <tbody>
        {records.map(r => (
          <tr key={r.id} className="border-t border-slate-800">
            <td className="p-2 font-mono">{r.sequence_number}</td>
            <td className="p-2 text-slate-300">{r.kind === 'verifactu' ? 'VeriFactu' : 'TBAI ' + r.kind.replace('ticket_bai_', '')}</td>
            <td className="p-2 text-xs text-slate-400 font-mono">{r.emitted_at.replace('T', ' ').slice(0, 16)}</td>
            <td className="p-2"><span className={`rounded px-2 py-0.5 text-xs ${statusColor(r.status)}`}>{r.status}</span></td>
            <td className="p-2 text-xs">
              {r.qr_short_code && r.qr_url && (
                <a href={r.qr_url} target="_blank" rel="noopener" className="font-mono text-positec-accent hover:underline">{r.qr_short_code}</a>
              )}
            </td>
            <td className="p-2 text-right">
              {r.status !== 'accepted' && r.status !== 'pending' && (
                <button onClick={() => retry(r.id)} className="text-xs text-positec-accent hover:underline">Reintentar</button>
              )}
            </td>
          </tr>
        ))}
        {records.length === 0 && <tr><td colSpan={6} className="text-slate-500 italic p-3 text-center">Sin registros fiscales emitidos</td></tr>}
      </tbody>
    </table>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

// =====================================================================
// Reports panel (Fase 10)
// =====================================================================

type ReportType =
  | 'daily-sales' | 'hourly' | 'by-employee' | 'by-center' | 'by-product'
  | 'by-family' | 'payment-methods' | 'taxes' | 'cancellations' | 'promotions'
  | 'top-unsold' | 'stock-movements' | 'by-sales-location';

const REPORT_LABELS: Record<ReportType, string> = {
  'daily-sales':       'Ventas por día',
  'hourly':            'Distribución por hora',
  'by-employee':       'Ventas por empleado',
  'by-center':         'Ventas por centro',
  'by-product':        'Ventas por producto',
  'by-family':         'Ventas por familia',
  'by-sales-location': 'Ventas por mesa/ubicación',
  'payment-methods':   'Formas de pago',
  'taxes':             'Desglose de IVA',
  'cancellations':     'Anulaciones',
  'promotions':        'Promociones aplicadas',
  'top-unsold':        'Productos sin venta',
  'stock-movements':   'Movimientos de stock',
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function firstOfMonthIso(): string {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function ReportsPanel({ client }: { client: PositecClient }) {
  const [type, setType] = useState<ReportType>('daily-sales');
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [date, setDate] = useState(todayIso());
  const [days, setDays] = useState(30);
  const [data, setData] = useState<{ rows: any[]; totals?: any } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true); setErr(null);
    try {
      let r: any;
      switch (type) {
        case 'daily-sales':       r = await client.reportDailySales(from, to); break;
        case 'hourly':            r = await client.reportHourly(date); break;
        case 'by-employee':       r = await client.reportByEmployee(from, to); break;
        case 'by-center':         r = await client.reportByCenter(from, to); break;
        case 'by-product':        r = await client.reportByProduct(from, to, 50); break;
        case 'by-family':         r = await client.reportByFamily(from, to); break;
        case 'by-sales-location': r = await client.reportBySalesLocation(from, to); break;
        case 'payment-methods':   r = await client.reportPaymentMethods(from, to); break;
        case 'taxes':             r = await client.reportTaxes(from, to); break;
        case 'cancellations':     r = await client.reportCancellations(from, to); break;
        case 'promotions':        r = await client.reportPromotions(from, to); break;
        case 'top-unsold':        r = await client.reportTopUnsold(days, 50); break;
        case 'stock-movements':   r = await client.reportStockMovements(from, to); break;
      }
      setData(r);
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e.message ?? 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const usesDate = type === 'hourly';
  const usesDays = type === 'top-unsold';
  const usesRange = !usesDate && !usesDays;

  const csvUrl = client.reportCsvUrl(type, usesDate ? { date } : usesDays ? { days: String(days) } : { from, to });

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
      <aside className="space-y-1">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Informes</h3>
        {(Object.keys(REPORT_LABELS) as ReportType[]).map(t => (
          <button key={t} onClick={() => setType(t)}
            className={`block w-full text-left text-sm rounded px-3 py-1.5 ${type === t ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-900'}`}>
            {REPORT_LABELS[t]}
          </button>
        ))}
      </aside>

      <section className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="text-base font-medium flex-1">{REPORT_LABELS[type]}</h2>
          {usesRange && (<>
            <Field label="Desde">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm" />
            </Field>
            <Field label="Hasta">
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm" />
            </Field>
          </>)}
          {usesDate && (
            <Field label="Fecha">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm" />
            </Field>
          )}
          {usesDays && (
            <Field label="Días sin vender">
              <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 30)} className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm w-20" />
            </Field>
          )}
          <button onClick={load} disabled={loading} className="rounded bg-positec-accent px-4 py-1.5 text-sm font-medium text-slate-900 disabled:opacity-50">
            {loading ? '…' : 'Calcular'}
          </button>
          {data?.rows && data.rows.length > 0 && (
            <a href={csvUrl} target="_blank" rel="noopener" className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800">
              ⬇ CSV
            </a>
          )}
        </div>

        {err && <p className="rounded bg-red-950/40 px-3 py-2 text-xs text-red-300">{err}</p>}

        {data && (
          <ReportTable rows={data.rows ?? []} totals={data.totals} type={type} />
        )}
      </section>
    </div>
  );
}

function ReportTable({ rows, totals, type }: { rows: any[]; totals?: any; type: ReportType }) {
  if (!rows || rows.length === 0) {
    return <p className="text-slate-500 italic text-sm py-4 text-center">Sin datos en el rango seleccionado.</p>;
  }
  const cols = Object.keys(rows[0]);

  const fmt = (k: string, v: any): string => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') {
      if (k === 'total' || k === 'subtotal_net' || k === 'tax' || k === 'tax_total' || k === 'discount_total' || k === 'tip_total' || k === 'avg_ticket' || k === 'tips' || k === 'net' || k === 'discount_total' || k === 'cost_total' || k === 'base' || k === 'amount') {
        return v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
      }
      if (k === 'tax_rate' || k === 'rate') return v.toFixed(2) + ' %';
      if (k === 'units') return v.toLocaleString('es-ES', { maximumFractionDigits: 2 });
      return v.toLocaleString('es-ES');
    }
    if (typeof v === 'string' && v.length > 40) return v.slice(0, 40) + '…';
    return String(v);
  };

  return (
    <div className="overflow-auto max-h-[60vh]">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-500 sticky top-0 bg-slate-900">
          <tr>
            {cols.map(c => <th key={c} className="text-left p-2 font-medium">{c.replace(/_/g, ' ')}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-slate-800 hover:bg-slate-900/40">
              {cols.map(c => <td key={c} className="p-2 font-mono text-xs">{fmt(c, row[c])}</td>)}
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot className="border-t-2 border-slate-700">
            <tr className="bg-slate-900/70">
              <td className="p-2 text-xs uppercase text-slate-500 font-bold">Totales</td>
              {cols.slice(1).map(c => (
                <td key={c} className="p-2 font-mono text-xs text-positec-accent">
                  {totals[c] !== undefined ? fmt(c, totals[c]) : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// =====================================================================
// Multilocal panel (Fase 13)
// =====================================================================

function MultilocalPanel({ client }: { client: PositecClient }) {
  const [orgs, setOrgs] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<any | null>(null);
  const [zones, setZones] = useState<any[]>([]);
  const [consolidated, setConsolidated] = useState<any | null>(null);
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  async function loadOrgs() {
    setLoading(true); setErr(null);
    try {
      const r = await client.listOrganizations();
      setOrgs(r.organizations);
      if (r.organizations.length > 0 && !selectedOrg && r.organizations[0]) {
        setSelectedOrg(r.organizations[0].id);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadDashboard(orgId: string) {
    try {
      const [d, z, c] = await Promise.all([
        client.organizationDashboard(orgId),
        client.listZones(orgId),
        client.consolidatedReport(orgId, from, to),
      ]);
      setDashboard(d);
      setZones(z.zones);
      setConsolidated(c);
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e.message);
    }
  }

  async function createOrg() {
    if (!newName) return;
    try {
      await client.createOrganization({ name: newName });
      setNewName(''); setShowCreate(false);
      loadOrgs();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Error creando organización');
    }
  }

  useEffect(() => { loadOrgs(); }, []);
  useEffect(() => { if (selectedOrg) loadDashboard(selectedOrg); }, [selectedOrg, from, to]);

  if (loading) return <p className="text-slate-400">Cargando organizaciones…</p>;
  if (err) return <p className="rounded bg-red-950/40 px-3 py-2 text-xs text-red-300">{err}</p>;

  return (
    <div className="space-y-4">
      {/* Selector de organización */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 flex items-center gap-3">
        <label className="text-sm text-slate-400">Organización:</label>
        <select
          value={selectedOrg ?? ''}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm flex-1"
        >
          {orgs.length === 0 && <option value="">— sin organizaciones —</option>}
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="rounded bg-positec-accent px-3 py-1.5 text-sm font-medium text-slate-900"
        >
          + Nueva
        </button>
      </div>

      {showCreate && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 flex items-center gap-2">
          <input
            placeholder="Nombre de la organización"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1 rounded border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm"
          />
          <button
            onClick={createOrg}
            disabled={!newName}
            className="rounded bg-positec-accent px-3 py-1.5 text-sm font-medium text-slate-900 disabled:opacity-40"
          >
            Crear
          </button>
        </div>
      )}

      {dashboard && (
        <>
          {/* Totales */}
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Locales" value={dashboard.totals.locales.toString()} />
            <Stat label="Tickets 30d" value={dashboard.totals.tickets_30d.toString()} />
            <Stat label="Ingresos 30d" value={`${dashboard.totals.revenue_30d.toFixed(2)} €`} accent />
          </div>

          {/* Zonas */}
          <section className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
            <h3 className="text-sm font-medium mb-2">Zonas ({zones.length})</h3>
            <div className="flex flex-wrap gap-2">
              {zones.map(z => (
                <span key={z.id} className="rounded bg-slate-800 px-3 py-1 text-xs">
                  {z.name}{z.code ? ` · ${z.code}` : ''}
                </span>
              ))}
              {zones.length === 0 && <p className="text-xs text-slate-500 italic">Sin zonas configuradas</p>}
            </div>
          </section>

          {/* Locales */}
          <section className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
            <h3 className="text-sm font-medium mb-2">Locales ({dashboard.locales.length})</h3>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-2">Local</th>
                  <th className="text-left p-2">Ciudad</th>
                  <th className="text-left p-2">Zona</th>
                  <th className="text-right p-2">Tickets 30d</th>
                  <th className="text-right p-2">Ingresos 30d</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.locales.map((l: any) => (
                  <tr key={l.id} className="border-t border-slate-800">
                    <td className="p-2">{l.name}</td>
                    <td className="p-2 text-slate-400">{l.city}</td>
                    <td className="p-2 text-slate-400">{l.zone ?? '—'}</td>
                    <td className="p-2 text-right font-mono">{l.tickets_30d}</td>
                    <td className="p-2 text-right font-mono text-positec-accent">{l.revenue_30d.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Consolidado */}
          <section className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
            <div className="flex items-end gap-3 mb-3">
              <h3 className="text-sm font-medium flex-1">Consolidado por locale</h3>
              <Field label="Desde">
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs" />
              </Field>
              <Field label="Hasta">
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs" />
              </Field>
            </div>
            {consolidated && consolidated.rows.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2">Local</th>
                    <th className="text-right p-2">Tickets</th>
                    <th className="text-right p-2">Neto</th>
                    <th className="text-right p-2">IVA</th>
                    <th className="text-right p-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {consolidated.rows.map((r: any) => (
                    <tr key={r.locale_id} className="border-t border-slate-800">
                      <td className="p-2">{r.name}</td>
                      <td className="p-2 text-right font-mono">{r.tickets}</td>
                      <td className="p-2 text-right font-mono">{r.net.toFixed(2)} €</td>
                      <td className="p-2 text-right font-mono">{r.tax.toFixed(2)} €</td>
                      <td className="p-2 text-right font-mono text-positec-accent">{r.total.toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-slate-700">
                  <tr className="bg-slate-900/70">
                    <td className="p-2 font-bold">TOTAL</td>
                    <td className="p-2 text-right font-mono">{consolidated.totals.tickets}</td>
                    <td className="p-2 text-right font-mono">{consolidated.totals.net.toFixed(2)} €</td>
                    <td className="p-2 text-right font-mono">{consolidated.totals.tax.toFixed(2)} €</td>
                    <td className="p-2 text-right font-mono text-positec-accent">{consolidated.totals.total.toFixed(2)} €</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="text-xs text-slate-500 italic">Sin datos en el rango seleccionado</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent ? 'text-positec-accent' : ''}`}>{value}</div>
    </div>
  );
}
